import React, { useRef, useEffect, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { RequireReferralsEnabled } from "components/access/RequireReferralsEnabled";
import { ErrorReportedModal, Loader, SelectedNavIconGradientDef } from "components";
import { TradeShowsSkeleton } from "pages/internal/GtmTradeShowsPage/tradeShowsShell";
import { SocialSkeleton } from "pages/internal/GtmSocialPage/socialShell";

/**
 * The internal docs pages are lazy, and that is load-bearing rather than a perf nicety.
 *
 * They are the only importers of `docs/sources.internal`, which carries the internal
 * MANIFEST — every internal guide's title, excerpt and heading text. Internal excerpts
 * include lines like "the backend @Permissions guard is the real security boundary; UI
 * gating is convenience only", and internal headings name the steps of a credential
 * runbook. Imported eagerly here, all of that shipped in the main bundle to every
 * customer, even though the pages themselves were correctly gated by <InternalRoute>.
 *
 * Splitting the guide BODIES into a lazy chunk was not enough: the summary of a body is
 * still the body's content. Deferring the pages is what keeps the manifest in the same
 * chunk as the bodies, so a session that never opens /internal/docs never fetches either.
 *
 * This is a DEFAULT-fetch boundary, not access control — the chunk URL is still reachable
 * by anyone who guesses it. `InternalRoute` gates the route, not the asset. Internal
 * guides must therefore stay operationally sensitive and never secret.
 */
const InternalDocsPage = lazy(() =>
  import("pages/internal/InternalDocsPage").then(m => ({ default: m.InternalDocsPage })),
);
// VRG-673 — lazy for the same reason as the docs pages: it imports a ~120 KB event snapshot
// that must not ride in the entry bundle. The fallback is its own skeleton (§19), imported
// eagerly from a module that does NOT import the snapshot.
const GtmTradeShowsPage = lazy(() =>
  import("pages/internal/GtmTradeShowsPage").then(m => ({ default: m.GtmTradeShowsPage })),
);
// Social: lazy for the same reason (it imports the generated strategy snapshot); skeleton eager.
const GtmSocialPage = lazy(() =>
  import("pages/internal/GtmSocialPage").then(m => ({ default: m.GtmSocialPage })),
);
const InternalDocPage = lazy(() =>
  import("pages/internal/InternalDocsPage").then(m => ({ default: m.InternalDocPage })),
);

import { useColorMode, useUserProfileQuery, useTextZoom } from "hooks";
import {
  ROUTES,
  analyticsIndexTarget,
  legacyUsageTarget,
  legacyAccessTarget,
  legacyInsidersTarget,
  legacyArticleTarget,
  legacyArticlesTarget,
  legacyContentTarget,
  legacyPeopleReferralsTarget,
  legacyOutreachCampaignTarget,
  legacyOutreachTarget,
  salesCampaignsIndexTarget,
} from "routes";
import { useTrackRouteForRecovery } from "utils";

import { CartProvider, DevToolsProvider } from 'contexts'
import { GlossaryProvider } from 'docs/GlossaryProvider'
// `LayoutAuth` is no longer routed here: SIGN_UP_SUCCESS was its last consumer
// in this file and now sits under LayoutAuthAmbient with the rest of the auth
// screens. The layout itself is kept — it is still exported from `layouts`.
import { Layout, LayoutAuthAmbient } from "layouts";
import {
  ProtectedRoute,
  UnProtectedRoute,
  InternalRoute,
  SettingsRoute,
} from "router";

import {
  AnalyticsPage,
  BillingPlanChangePage,
  BillingPlanCheckoutPage,
  CertificateActivityPage,
  CertificatePage,
  CertificatesPage,
  CompaniesPage,
  CompanyEditPage,
  CompanyPage,
  HomePage,
  InternalUsersPage,
  InternalTagsPage,
  CustomerDocPage,
  GlossaryPage,
  SupportRequestPage,
  SupportIntakeProvider,
  useSupportIntake,
  SupportBoardRedirect,
  InternalSupportPage,
  InternalSupportRequestPage,
  InternalAnnouncementsPage,
  InternalAnnouncementEditPage,
  WhatsNewPage,
  WhatsNewAnnouncementPage,
  ReaderSetup,
  IntegrationPage,
  InternalAnalyticsPage,
  GtmChannelsPage,
  GtmSeoPage,
  GtmInsightsPage,
  ArticleDetailPage,
  GtmAffiliatesPage,
  GtmSalesPage,
  GtmOutreachCampaignPage,
  InternalUsagePage,
  LabsPage,
  InvoicePage,
  NotFoundPage,
  TrialEndedPage,
  PublicHelpPage,
  OrdersPage,
  OrderDetailPage,
  CreateOwnerPage,
  OwnerEditPage,
  OwnerPage,
  PeoplePage,
  SalesPage,
  PasswordSetup,
  VerifyEmailPage,
  ProductEditPage,
  ProductPage,
  ProductsPage,
  SearchPage,
  SettingsPage,
  SignInPage,
  SignUpPage,
  SignupSuccessPage,
  AuthCallbackPage,
  StudioPage,
  InternalInvoicesPage,
  InternalInvoicePage,
  InternalDemosPage,
  SignupsPage,
  InternalPeoplePage,
  TagInventoryPage,
  TagsOrderPage,
  TagsOrderConfirmationPage,
  TagsPage,
  TagsIndexRedirect,
  TagsShopCartPage,
  TagsShopProductPage,
  PackagesPage,
  RolePage,
  UserPage,
  TagsShopCheckoutPage,
  SubscriptionsPage,
  InternalIntegrationsPage,
  InternalMarketPage,
  CompetitorResearchPage,
  InternalMarketContentPage,
} from "pages";

import { VeraThemeProvider } from "@repo/ui";
import { ErrorBoundary } from "./components/ErrorBoundary";
// Imported by PATH, not through the `utils` barrel, for the reason that barrel's own
// comment gives about `devSurface`: the guard suite stubs the barrel wholesale, and
// this module reads `window.location` at import time.
import { HANDOFF_COLOR_MODE, ENTERED_PRE_AUTH } from "./utils/colorModeHandoff";

// Hydrates the shared theme provider with the saved profile.theme on login/reload.
// Must render inside <VeraThemeProvider> (useColorMode throws if used outside).
// Runs once per profile id so it never clobbers an in-session user toggle.
const ThemeHydrator = () => {
  const { data: profile } = useUserProfileQuery()
  const { setMode } = useColorMode()
  const initializedProfileIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (profile?.id && profile.theme && initializedProfileIdRef.current !== profile.id) {
      initializedProfileIdRef.current = profile.id
      setMode(profile.theme)
    }
  }, [profile?.id, profile?.theme, setMode])

  return null
}

// VRG-338: `HomeRedirect` lived here and sent everyone to /certificates — an empty table for
// a brand that has just signed up. `/` now renders a real Home (`HomePage`), which carries the
// internal-user and in-space branches this component used to own, so they are not lost.
// Do not reintroduce a redirect here; VRG-339 collapses the landing rule to one source.

// VERA-914: /owners was renamed to /people. Redirect legacy links, carrying the
// query string (?tab=leads, ?filters=product:isAnyOf:<id>) across so deep links work.
const LegacyOwnersRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`${ROUTES.OWNERS}${search}`} replace />;
};

/*
  VRG-684 — GTM addresses that moved inside GTM. Each keeps the visitor's query string (the pure
  targets live in `routes/gtmRedirects.ts`, where the guard exercises them):
    /internal/gtm/outreach(?…)            → /internal/gtm/sales?tab=campaigns(&…)
    /internal/gtm/outreach/:id(?…)        → /internal/gtm/sales/campaigns/:id(?…)
    /internal/gtm/sales/campaigns         → /internal/gtm/sales?tab=campaigns  (a campaign's breadcrumb)
    /internal/gtm/insiders(?…)            → /internal/gtm/affiliates(?…)
  VRG-713 — the Outreach tab became Campaigns, so VRG-684's own addresses redirect too, one hop:
    /internal/gtm/sales/outreach(?…)      → /internal/gtm/sales?tab=campaigns(&…)
    /internal/gtm/sales/outreach/:id(?…)  → /internal/gtm/sales/campaigns/:id(?…)
    /internal/gtm/sales?tab=outreach      → ?tab=campaigns (inside GtmSalesPage: a query string)
  `/internal/access?tab=referrals` is handled inside the Access page, since a <Route> cannot
  match on a query string (same reason as `/internal/analytics?tab=…`). VRG-727 — the page's old
  addresses (`/internal/people`, `/internal/signups`) → `/internal/access`, one hop.
*/
const LegacyOutreachRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={legacyOutreachTarget(search)} replace />;
};
const LegacyOutreachCampaignRedirect = () => {
  const { id = "" } = useParams();
  const { search } = useLocation();
  return <Navigate to={legacyOutreachCampaignTarget(id, search)} replace />;
};
const SalesCampaignsIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={salesCampaignsIndexTarget(search)} replace />;
};
/** VRG-684 — `/internal` and `/internal/analytics` → Analytics → Website, in one hop. */
const AnalyticsIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={analyticsIndexTarget(search)} replace />;
};
/** VRG-684 — `/internal/usage(?tab=…)` → Analytics → Product, params kept. */
const LegacyUsageRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={legacyUsageTarget(search)} replace />;
};
/** People and the classic Signups page: `?tab=referrals` moved to Affiliates → Refer & earn. */
const PeopleReferralsRedirect = ({ children }: { children: React.ReactElement }) => {
  const { search } = useLocation();
  const to = legacyPeopleReferralsTarget(search);
  return to ? <Navigate to={to} replace /> : children;
};
/** VRG-727 — /internal/people and /internal/signups → /internal/access, params and state kept. */
const LegacyAccessRedirect = () => {
  const { search, state } = useLocation();
  return <Navigate to={legacyAccessTarget(search)} replace state={state} />;
};
/** VRG-733's pre-merge article addresses → `/internal/gtm/insights/articles/:section/:slug`. */
const LegacyArticleRedirect = ({ cover = false }: { cover?: boolean }) => {
  const { section = "", slug = "" } = useParams();
  const { search } = useLocation();
  return <Navigate to={legacyArticleTarget(section, slug, search, cover)} replace />;
};
/**
 * VRG-674's `/internal/gtm/articles?tab=…` → the Insights tab that holds it, params kept and
 * `tab` dropped (the tab is the path now). VRG-751.
 */
const LegacyArticlesRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={legacyArticlesTarget(search)} replace />;
};
/** VRG-751 — VRG-734's `/internal/gtm/content[/<tab>]` → the same tab under `/insights`. */
const LegacyContentRedirect = () => {
  const { pathname, search } = useLocation();
  return <Navigate to={legacyContentTarget(pathname, search) ?? ROUTES.INTERNAL_GTM_ARTICLES} replace />;
};

const LegacyInsidersRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={legacyInsidersTarget(search)} replace />;
};

/**
 * VRG-221: settings leaves lost their scope segment (`/settings/company/users` →
 * `/settings/users`). Every old URL still resolves, because bookmarks, in-flight
 * emails, the committed docs guides and anything a user pasted into Slack all point
 * at the old shape — a rename that 404s its own history is a rename that gets
 * reverted.
 *
 * ONE splat route per scope rather than ~20 hand-written `<Navigate>` pairs, which is
 * the difference between "the paths I remembered" and "all of them": it catches the
 * parameterised ones (`users/:id`, `roles/:name`, `integrations/:name`,
 * `billing/invoices/:id`) and any nesting below them for free, and it cannot drift
 * out of sync with the constants above because it does not enumerate them.
 *
 * `search` and `hash` are carried across for the same reason as the owners redirect:
 * `?tab=field-mapping`, `?create=1` and `?provider=shopify` are all real in-app deep
 * links, and dropping the query would silently land the user on a different view than
 * the link promised.
 */
const SETTINGS_SCOPE_PREFIXES = [
  ROUTES.SETTINGS_PERSONAL,
  ROUTES.SETTINGS_COMPANY,
] as const;

const flattenLegacySettingsPath = (pathname: string): string | null => {
  const prefix = SETTINGS_SCOPE_PREFIXES.find(p => pathname.startsWith(`${p}/`));
  if (!prefix) return null;
  const rest = pathname.slice(prefix.length + 1);
  // The one path whose slug also changed, not just its depth: personal support is
  // `my-requests` because a bare `support` would collide with the company section.
  // Handled here rather than by the generic strip, which would send both to the same
  // place and quietly hand every "my requests" bookmark the company queue — a page the
  // user may not even have permission to see.
  if (rest === "support" && prefix === ROUTES.SETTINGS_PERSONAL) {
    return ROUTES.SETTINGS_PERSONAL_SUPPORT;
  }
  return `${ROUTES.SETTINGS}/${rest}`;
};

/** `/settings/field-mapping` → `/settings/fields`, query and hash intact. */
const LegacyFieldMappingRedirect = () => {
  const { search, hash } = useLocation();
  return (
    <Navigate to={`${ROUTES.SETTINGS_COMPANY_FIELDS}${search}${hash}`} replace />
  );
};

const LegacySettingsRedirect = () => {
  const { pathname, search, hash } = useLocation();
  const to = flattenLegacySettingsPath(pathname);
  // Unreachable via the routes below (they only match under a scope prefix), but a
  // bare Navigate to `null` would be a blank screen — fall back to the settings root.
  return <Navigate to={`${to ?? ROUTES.SETTINGS}${search}${hash}`} replace />;
};

// VRG-226: `/support/new` is a route-driven MODAL, not a page. While the URL is
// the intake, `<Routes>` keeps rendering the page the user was already on (or
// /support on a cold load) so the thing they are describing stays on screen; the
// modal itself is rendered by Layout. See pages/support/NewSupportRequest.
//
// VRG-726: `routesLocation` is NEVER undefined — at every other URL it is the real
// location. Passing `undefined` while closed and a location while open changed the
// element react-router renders at the root of this tree, so opening the modal
// REMOUNTED the page behind it and threw away unsaved edits. See routesLocation.ts;
// pinned by test/support-queue-source.guard.spec.ts.
const AppRoutes = () => {
  const { routesLocation } = useSupportIntake();
  return (
            <Routes
              location={routesLocation}
              future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
            >
              <Route element={<ProtectedRoute />}>
                {/* onboarding step — protected but no admin chrome (VERA-660).

                    VRG-646: under LayoutAuthAmbient, INSIDE ProtectedRoute. New
                    SSO users land here straight from AUTH_CALLBACK, and
                    PasswordSetup's shell is transparent (the backdrop has been
                    the layout's since VERA-1095), so without this layout it
                    rendered on the bare page. ProtectedRoute still wraps it, so
                    the session requirement is unchanged; the layout adds only
                    the backdrop and the theme toggle. Kept honest by
                    `test/pre-auth-routes.guard.spec.ts`. */}
                <Route element={<LayoutAuthAmbient />}>
                  <Route path={ROUTES.PASSWORD_SETUP} element={<PasswordSetup />} />
                </Route>
                <Route element={<Layout />}>
                  <Route path={ROUTES.HOME} element={<HomePage />} />

                  {/* main */}
                  <Route path={ROUTES.SEARCH} element={<SearchPage />} />
                  <Route path={ROUTES.TAGS} element={<TagsIndexRedirect />} />
                  <Route
                    path={ROUTES.TAGS_SHOP}
                    element={<TagsPage tab="shop" />}
                  />
                  <Route
                    path={ROUTES.TAGS_INVENTORY}
                    element={<TagsPage tab="inventory" />}
                  />
                  <Route
                    path={ROUTES.TAG_INVENTORY(":sku")}
                    element={<TagInventoryPage />}
                  />
                  <Route
                    path={ROUTES.TAGS_SHOP_PRODUCT(":id")}
                    element={<TagsShopProductPage />}
                  />
                  <Route
                    path={ROUTES.TAGS_SHOP_CART}
                    element={<TagsShopCartPage />}
                  />
                  <Route
                    path={ROUTES.TAGS_SHOP_CHECKOUT}
                    element={<TagsShopCheckoutPage />}
                  />
                  <Route
                    path={ROUTES.TAGS_ORDERS}
                    element={<TagsPage tab="orders" />}
                  />
                  <Route
                    path={ROUTES.TAGS_ORDER_CONFIRMATION(":reconciliationId")}
                    element={<TagsOrderConfirmationPage />}
                  />
                  <Route
                    path={ROUTES.TAGS_ORDER(":id")}
                    element={<TagsOrderPage />}
                  />
                  <Route path={ROUTES.PRODUCTS} element={<ProductsPage />} />
                  <Route path={ROUTES.PRODUCT(":id")} element={<ProductPage />} />
                  <Route
                    path={ROUTES.PRODUCT_CREATE}
                    element={<ProductEditPage page="create" />}
                  />
                  <Route
                    path={ROUTES.PRODUCT_EDIT(":id")}
                    element={<ProductEditPage page="edit" />}
                  />
                  <Route
                    path={ROUTES.CERTIFICATES}
                    element={<CertificatesPage />}
                  />
                  {/* VRG-430: must precede CERTIFICATE(":id") — a static "/certificates/activity"
                      route registered AFTER the dynamic ":id" route would never be reached, since
                      the dynamic route matches "activity" as an id first. */}
                  <Route
                    path={ROUTES.COMPANY_CERTIFICATE_ACTIVITY}
                    element={<CertificateActivityPage />}
                  />
                  <Route
                    path={ROUTES.CERTIFICATE(":id")}
                    element={<CertificatePage tab="details" />}
                  />
                  <Route
                    path={ROUTES.CERTIFICATE_PROVENANCE(":id")}
                    element={<CertificatePage tab="provenance" />}
                  />
                  <Route
                    path={ROUTES.CERTIFICATE_SCANS(":id")}
                    element={<CertificatePage tab="scans" />}
                  />
                  <Route
                    path={ROUTES.CERTIFICATE_ACTIVITY(":id")}
                    element={<CertificatePage tab="activity" />}
                  />
                  {/* VRG-455 — recorded sales awaiting the buyer's scan. */}
                  <Route path={ROUTES.SALES} element={<SalesPage />} />
                  <Route path={ROUTES.OWNERS} element={<PeoplePage />} />
                  {/* VERA-914: legacy /owners → /people, preserving query (?tab, ?filters). */}
                  <Route path="/owners" element={<LegacyOwnersRedirect />} />
                  <Route path={ROUTES.OWNER(":id")} element={<OwnerPage />} />
                  <Route path={ROUTES.OWNER_CREATE} element={<CreateOwnerPage />} />
                  <Route
                    path={ROUTES.OWNER_EDIT(":id")}
                    element={<OwnerEditPage page="edit" />}
                  />
                  <Route path={ROUTES.ANALYTICS} element={<AnalyticsPage />} />
                  {/* VERA-455: Inventory folded into Tags -> Inventory (§8 — it is tags
                      filtered by possession, a facet, not a first-class object). Redirected
                      rather than deleted: the sidebar entry is gone but bookmarks, the search
                      palette's older entries, and any link out there must not 404. */}
                  <Route
                    path={ROUTES.INVENTORY}
                    element={<Navigate to={ROUTES.TAGS_INVENTORY} replace />}
                  />
                  <Route path={ROUTES.STUDIO} element={<StudioPage />} />

                  {/* support & knowledge base (VRG-224) — routes registered here so
                      Epics B/C don't each have to edit routes.ts. Both the docs
                      (VRG-225) and the support centre (VRG-226) have now landed, so
                      every route below is a real page and no placeholder remains. */}
                  {/* /docs is the overview GUIDE, in the reader layout — not a grid of
                      cards. The grid listed the same 16 guides the left nav lists on every
                      page, so it answered "what exists" twice and "where do I start"
                      nowhere.
                      VRG-402: /internal/docs now does the same. It was the last card grid,
                      kept on the grounds that "four runbooks have no reading order to
                      recommend" — there are fifteen now, so that had expired on its own
                      terms. Both sites land on their own overview guide; the grid component
                      is deleted. */}
                  <Route path={ROUTES.DOCS} element={<CustomerDocPage overview />} />
                  {/* Ahead of the :category/:slug route below. React Router v6 ranks by
                      specificity rather than declaration order, so a static path already
                      beats a dynamic one — but reading it in this order is how you can
                      TELL, and `reference` is reserved out of the guide category set so
                      the two can never actually contend. */}
                  <Route path={ROUTES.DOCS_GLOSSARY} element={<GlossaryPage />} />
                  <Route
                    path={`${ROUTES.DOCS}/:category/:slug`}
                    element={<CustomerDocPage />}
                  />
                  {/* VRG-226 replaces Epic A's placeholders with the real pages.
                      The literal /support/requests stays declared before
                      /support/:id. React Router v6 ranks static above dynamic so
                      it resolves either way, but the ordering documents the intent.

                      There is deliberately NO route for /support/new: the intake
                      is a modal over whatever is already rendered, so at that URL
                      <Routes> is matching the BACKGROUND location instead (see
                      AppRoutes above). Adding a route element back here would
                      resurrect the page and give us two renderings of one form. */}
                  {/* /support is no longer a page of its own — the list now lives as two
                      settings sections, Personal → Support and Company → Support (VRG-221).
                      It stays declared as a REDIRECT rather than being deleted because it is
                      the URL every existing bookmark, the Help popover's old target and the
                      intake modal's background location all point at; removing it would
                      404 them. `replace` so it does not leave a dead entry in history that
                      Back would bounce off.
                      It resolves to the PERSONAL scope, which is the one every user can see
                      — sending an unpermissioned user to the company view would land them on
                      a section their nav row shows as disabled. */}
                  <Route
                    path={ROUTES.SUPPORT}
                    element={<Navigate to={ROUTES.SETTINGS_PERSONAL_SUPPORT} replace />}
                  />
                  {/* VRG-324 — the customer feature board is RETIRED. The URL stays
                      as a permission-aware redirect rather than being deleted: it is
                      in bookmarks, in What's-new announcement bodies ("you asked for
                      this") and in request threads we ourselves wrote, so 404-ing it
                      would strand people on links we published. Where it sends them
                      depends on the caller — a member without `View company requests`
                      must not land on a section their nav shows as disabled — which
                      is why this is a component and not a bare <Navigate>. See
                      SupportBoardRedirect. */}
                  <Route
                    path={ROUTES.SUPPORT_REQUESTS_BOARD}
                    element={<SupportBoardRedirect />}
                  />
                  <Route path={`${ROUTES.SUPPORT}/:id`} element={<SupportRequestPage />} />
                  <Route path={ROUTES.READER_SETUP} element={<ReaderSetup />} />
                  {/* VRG-221 — the in-app changelog archive. A top-level company
                      route, not under /support: it is product news, not a request or a
                      conversation. Ungated on purpose — reading "what's new" cannot be
                      permission-gated without hiding from a user the news that the
                      thing THEY asked for shipped. Per-announcement audience control
                      happens server-side instead. */}
                  <Route path={ROUTES.WHATS_NEW} element={<WhatsNewPage />} />
                  {/* One announcement in full, and the only shareable address an
                      announcement has. The archive clamps long bodies to three lines,
                      so this is where the rest of a long notice lives. Ungated for the
                      same reason as the archive above — the per-announcement audience
                      gate is applied server-side, and a gated one 404s here rather than
                      403ing, so the route cannot confirm what it is hiding. */}
                  <Route
                    path={`${ROUTES.WHATS_NEW}/:id`}
                    element={<WhatsNewAnnouncementPage />}
                  />

                  {/* settings — the whole block sits under <SettingsRoute> so an
                      internal Vera user cannot reach a brand's settings by URL, only by
                      finding the nav gone (VRG-395 AC5). Wrapping the block rather than
                      the ~22 company routes individually is deliberate: a settings route
                      added later is gated by construction, and the six pages here that
                      are NOT <SettingsPage> (UserPage, RolePage, InvoicePage, the two
                      billing-plan pages, IntegrationPage) are covered by the same wrapper
                      rather than each needing to remember. */}
                  <Route element={<SettingsRoute />}>
                    <Route
                      path={ROUTES.SETTINGS}
                      element={<SettingsPage tab="personal" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL}
                      element={<SettingsPage tab="personal" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL_PROFILE}
                      element={<SettingsPage tab="personal" />}
                    />
                    {/* VRG-490 (D6) — Settings → Security (password + two-factor). */}
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL_SECURITY}
                      element={<SettingsPage tab="personal" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL_APPEARANCE}
                      element={<SettingsPage tab="personal" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL_NOTIFICATIONS}
                      element={<SettingsPage tab="personal" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL_CONTROLS}
                      element={<SettingsPage tab="personal" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL_SUPPORT}
                      element={<SettingsPage tab="personal" />}
                    />
                    {/* VRG-338: the personal Activity page. Its route constant and its nav row
                        existed without this, so the sidebar offered a link straight to a 404. */}
                    <Route
                      path={ROUTES.SETTINGS_PERSONAL_ACTIVITY}
                      element={<SettingsPage tab="personal" />}
                    />
                    {/* VRG-655 — "Refer & earn": the native page replaced the
                        FirstPromoter iframe (VRG-657), which closes VRG-421 (superseded).
                        VRG-680: redirects to Settings while the launch switch is off. */}
                    <Route
                      path={ROUTES.REFERRALS}
                      element={
                        <RequireReferralsEnabled>
                          <SettingsPage tab="personal" />
                        </RequireReferralsEnabled>
                      }
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_GENERAL}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_USERS}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_USER(":id")}
                      element={<UserPage />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_ROLES}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_ROLE(":name")}
                      element={<RolePage />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_FEATURES}
                      element={<SettingsPage tab="company" />}
                    />
                    {/* TODO: add invoices breadcrumb route */}
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_BILLING}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_INVOICES}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_INVOICE(":id")}
                      element={<InvoicePage />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_PAYMENT_METHODS}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_BILLING_PLAN}
                      element={<BillingPlanChangePage />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_BILLING_PLAN_CHANGE_CHECKOUT}
                      element={<BillingPlanCheckoutPage />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_SUPPORT}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_ADDRESSBOOK}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_INSTALLATION}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_INTEGRATIONS}
                      element={<SettingsPage tab="company" />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_INTEGRATION(":name")}
                      element={<IntegrationPage />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_FIELDS}
                      element={<SettingsPage tab="company" />}
                    />
                    {/* VRG-459 — the brand-supplied figures analytics metrics are
                        calculated from. Its own settings section rather than a tab on
                        Fields; see the route constant for the taxonomy argument. */}
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_BUSINESS_FIGURES}
                      element={<SettingsPage tab="company" />}
                    />
                    {/* The pre-rename path. Its own `<Navigate>` rather than a case in
                        `flattenLegacySettingsPath`, because that helper answers a
                        different question (strip the scope segment) and only ever runs
                        under `/settings/personal/*` or `/settings/company/*` — this URL
                        has no scope segment to strip. `search`/`hash` carry across for
                        the same reason they do there: `?provider=shopify` and
                        `?highlight=sync` are live deep links. */}
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_FIELDMAPPING_LEGACY}
                      element={<LegacyFieldMappingRedirect />}
                    />
                    <Route
                      path={ROUTES.SETTINGS_COMPANY_ACTIVITY}
                      element={<SettingsPage tab="company" />}
                    />
                    {/* VRG-221 — legacy scoped settings URLs. Declared LAST of the
                        settings block so it reads as the fallback it is; React Router
                        v6 ranks by specificity rather than declaration order, so the
                        two static landing routes above (/settings/personal and
                        /settings/company, which are kept as real pages) still win over
                        these splats and are NOT redirected. Only paths BELOW a scope
                        prefix land here. */}
                    <Route
                      path={`${ROUTES.SETTINGS_PERSONAL}/*`}
                      element={<LegacySettingsRedirect />}
                    />
                    <Route
                      path={`${ROUTES.SETTINGS_COMPANY}/*`}
                      element={<LegacySettingsRedirect />}
                    />
                  </Route>

                  {/* internal - Vera employees only */}
                  <Route element={<InternalRoute />}>
                    {/* VRG-684 — `/internal` and `/internal/analytics` both rendered the Website
                        page; they now redirect to Analytics → Website (or, for an old
                        `?tab=seo|affiliates`, straight to its GTM page), params kept. */}
                    <Route path={ROUTES.INTERNAL} element={<AnalyticsIndexRedirect />} />
                    <Route path={`${ROUTES.INTERNAL_LABS}/:section?`} element={<LabsPage />} />
                    <Route path={ROUTES.INTERNAL_ANALYTICS} element={<AnalyticsIndexRedirect />} />
                    <Route path={ROUTES.INTERNAL_ANALYTICS_WEBSITE} element={<InternalAnalyticsPage />} />
                    {/* Product: Tours + Ask as two tabs (was `/internal/usage`, which redirects). */}
                    <Route path={ROUTES.INTERNAL_ANALYTICS_PRODUCT} element={<InternalUsagePage />} />
                    {/* VRG-672 — GTM. `/internal/analytics?tab=seo` redirects to the SEO
                        route from inside InternalAnalyticsPage (legacyAnalyticsSeoTarget),
                        since a <Route> cannot match on a query string. */}
                    <Route path={ROUTES.INTERNAL_GTM} element={<GtmChannelsPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_SEO} element={<GtmSeoPage />} />
                    {/* VRG-751 — Insights: Articles · Ideas · Interviews · Analytics, one page whose
                        tab is the path. The bare parent shows Articles. Both earlier generations of
                        address redirect in one hop with their params: VRG-674's
                        `/internal/gtm/articles?tab=` and VRG-734's `/internal/gtm/content/*`. */}
                    <Route path={ROUTES.INTERNAL_GTM_INSIGHTS} element={<Navigate to={ROUTES.INTERNAL_GTM_INSIGHTS_ANALYTICS} replace />} />
                    <Route path={ROUTES.INTERNAL_GTM_ARTICLES} element={<GtmInsightsPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_INSIGHTS_IDEAS} element={<GtmInsightsPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_INSIGHTS_INTERVIEWS} element={<GtmInsightsPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_INSIGHTS_ANALYTICS} element={<GtmInsightsPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_INSIGHTS_RULES} element={<GtmInsightsPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_ARTICLES_LEGACY} element={<LegacyArticlesRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_CONTENT_LEGACY} element={<LegacyContentRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_CONTENT_ARTICLES_LEGACY} element={<LegacyContentRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_CONTENT_VERAFIED_LEGACY} element={<LegacyContentRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_CONTENT_ANALYTICS_LEGACY} element={<LegacyContentRedirect />} />
                    {/* VRG-733 — one article (Overview, Cover tabs; under the GTM segment rule, internal_gtm_view), under Insights → Articles. Its pre-merge addresses redirect. */}
                    <Route path={ROUTES.INTERNAL_GTM_ARTICLE(':section', ':slug')} element={<ArticleDetailPage />} />
                    <Route path={`${ROUTES.INTERNAL_GTM_ARTICLES_LEGACY}/:section/:slug`} element={<LegacyArticleRedirect />} />
                    <Route path={`${ROUTES.INTERNAL_GTM_ARTICLES_LEGACY}/:section/:slug/cover`} element={<LegacyArticleRedirect cover />} />
                    {/* VRG-684 — Affiliates (was Insiders, VRG-678). `/internal/analytics?tab=affiliates`
                        lands here straight from InternalAnalyticsPage. */}
                    <Route path={ROUTES.INTERNAL_GTM_AFFILIATES} element={<GtmAffiliatesPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_INSIDERS_LEGACY} element={<LegacyInsidersRedirect />} />
                    {/* VRG-675 + VRG-684 + VRG-713 — Sales, with Campaigns as a tab and a campaign under it. */}
                    <Route path={ROUTES.INTERNAL_GTM_SALES} element={<GtmSalesPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_SALES_CAMPAIGNS} element={<SalesCampaignsIndexRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_SALES_CAMPAIGN(':id')} element={<GtmOutreachCampaignPage />} />
                    <Route path={ROUTES.INTERNAL_GTM_SALES_OUTREACH_LEGACY} element={<SalesCampaignsIndexRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_SALES_OUTREACH_CAMPAIGN_LEGACY(':id')} element={<LegacyOutreachCampaignRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_OUTREACH_LEGACY} element={<LegacyOutreachRedirect />} />
                    <Route path={ROUTES.INTERNAL_GTM_OUTREACH_CAMPAIGN_LEGACY(':id')} element={<LegacyOutreachCampaignRedirect />} />
                    <Route
                      path={ROUTES.INTERNAL_GTM_TRADE_SHOWS}
                      element={<Suspense fallback={<TradeShowsSkeleton />}><GtmTradeShowsPage /></Suspense>}
                    />
                    {/* VRG-735 — path tabs (`/:tab/:view`) under the parent. The bare parent renders
                        the page too, which redirects it (and VRG-672's `?tab=` URLs) through
                        `socialTarget`. */}
                    <Route
                      path={ROUTES.INTERNAL_GTM_SOCIAL}
                      element={<Suspense fallback={<SocialSkeleton />}><GtmSocialPage /></Suspense>}
                    />
                    <Route
                      path={`${ROUTES.INTERNAL_GTM_SOCIAL}/*`}
                      element={<Suspense fallback={<SocialSkeleton />}><GtmSocialPage /></Suspense>}
                    />
                    <Route path={ROUTES.INTERNAL_USAGE} element={<LegacyUsageRedirect />} />
                    {/*
                      `/internal/ask-stats` -> `/internal/analytics/product?tab=ask` (one hop;
                      it went through `/internal/usage` until VRG-684).

                      KEPT RATHER THAN DELETED because published staff guides and
                      glossary entries link to the old path; removing it would 404 them
                      until every reference is updated AND re-published (the docs artifact
                      is committed, so a stale one keeps serving — working-conventions
                      §43). `replace` so the dead path does not sit in history for the
                      Back button to return to.
                    */}
                    <Route
                      path={ROUTES.INTERNAL_ASK_STATS}
                      element={<Navigate to={`${ROUTES.INTERNAL_ANALYTICS_PRODUCT}?tab=ask`} replace />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_PACKAGES}
                      element={<PackagesPage />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_TAGS}
                      element={<InternalTagsPage />}
                    />
                    <Route path={ROUTES.INTERNAL_ORDERS} element={<OrdersPage />} />
                    <Route path={ROUTES.INTERNAL_ORDER(':id')} element={<OrderDetailPage />} />
                    <Route
                      path={ROUTES.INTERNAL_INVOICES}
                      element={<InternalInvoicesPage />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_INVOICE(':id')}
                      element={<InternalInvoicePage />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_DEMOS}
                      element={<InternalDemosPage />}
                    />
                    {/* VRG-401 — was the Labs section `/internal/labs/signups`.
                        `InternalRoute` admits platform_manager too, so SignupsPage keeps
                        the platform_admin guard Labs was enforcing for it. */}
                    {/* People (2026-10-01) replaced the Signups tabs with one table; VRG-727
                        (2026-10-20) renamed it Access, at /internal/access (the staff list
                        moved to /internal/team on 2026-10-01). /internal/people and
                        /internal/signups redirect there (`replace`, so Back does not bounce;
                        params and router state kept, `LegacyAccessRedirect`); the old page lives
                        on at /classic for comp / activations / reject and the forwarded
                        website fields. */}
                    <Route
                      path={ROUTES.INTERNAL_SIGNUPS}
                      element={<LegacyAccessRedirect />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_PEOPLE}
                      element={<LegacyAccessRedirect />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_SIGNUPS_CLASSIC}
                      element={<PeopleReferralsRedirect><SignupsPage /></PeopleReferralsRedirect>}
                    />
                    <Route
                      path={ROUTES.INTERNAL_ACCESS}
                      element={<PeopleReferralsRedirect><InternalPeoplePage /></PeopleReferralsRedirect>}
                    />
                    <Route
                      path={ROUTES.INTERNAL_USERS}
                      element={<InternalUsersPage />}
                    />
                    {/* <Route path={ROUTES.INTERNAL_USER(':username')} element={<CompanyPage />} /> */}
                    <Route
                      path={ROUTES.INTERNAL_COMPANIES}
                      element={<CompaniesPage />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_COMPANY(":id")}
                      element={<CompanyPage />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_CREATE_COMPANY}
                      element={<CompanyEditPage type="create" />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_EDIT_COMPANY(":id")}
                      element={<CompanyEditPage type="edit" />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_SUBSCRIPTIONS}
                      element={<SubscriptionsPage />}
                    />
                    {/* TODO(VERA-405): gate behind an internal 'integrations.view' permission once added to the catalog */}
                    <Route
                      path={ROUTES.INTERNAL_INTEGRATIONS}
                      element={<InternalIntegrationsPage />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_SUPPORT}
                      element={<InternalSupportPage />}
                    />
                    {/* One request on its own page, replacing the queue's inline
                        accordion — the internal twin of /support/:id, rendering the
                        same RequestThread. Declared after the list route; react-router
                        ranks by specificity rather than order, so the static
                        /internal/support still wins over this one's `:id`. */}
                    <Route
                      path={ROUTES.INTERNAL_SUPPORT_REQUEST(':id')}
                      element={<InternalSupportRequestPage />}
                    />
                    {/* VRG-221 — the staff authoring surface. Inside <InternalRoute>
                        like every other /internal/* route; the capability check on top
                        of that role gate is @Permissions(InternalAnnouncementsManage) on
                        the API, which 403s until the role grant is decided. */}
                    <Route
                      path={ROUTES.INTERNAL_ANNOUNCEMENTS}
                      element={<InternalAnnouncementsPage />}
                    />
                    {/* VRG-332 — the authoring form, promoted out of a dialog onto its
                        own two addresses. ONE component for both (§30): `page` switches
                        only the title, the verb and the mutation.

                        Static beats dynamic in react-router's ranking, so `/new` wins
                        over `/:id` whichever order these are declared in. */}
                    <Route
                      path={ROUTES.INTERNAL_ANNOUNCEMENT_CREATE}
                      element={<InternalAnnouncementEditPage page="create" />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_ANNOUNCEMENT_EDIT(':id')}
                      element={<InternalAnnouncementEditPage page="edit" />}
                    />
                    {/* VRG-461 (epic VRG-445) — the market-data corpus. Inside
                        <InternalRoute> like every other /internal/* route; the
                        capability check is @Permissions(InternalMarketIntelView) on
                        the API. VRG-537 adds the detail route below this one — the
                        "no detail route" half of this comment is now history, kept
                        so the original VRG-461 reasoning stays legible; see that
                        route's own comment. */}
                    <Route
                      path={ROUTES.INTERNAL_MARKET}
                      element={<InternalMarketPage />}
                    />
                    {/* VRG-744 — Competitor research (sidebar → Research). */}
                    <Route
                      path={ROUTES.INTERNAL_COMPETITORS}
                      element={<CompetitorResearchPage />}
                    />
                    {/* VRG-462/463 — the feed registry and the approval queue are now
                        TABS of the page above, not pages of their own (see that page's
                        docblock: three separate routes left no way back to the corpus).
                        Both paths still resolve, to the same element, which reads its
                        active tab off the pathname — so a pasted link opens the tab it
                        named instead of 404ing. Declared after the parent; react-router
                        ranks by specificity, so these static paths still win. */}
                    <Route
                      path={ROUTES.INTERNAL_MARKET_SOURCES}
                      element={<InternalMarketPage />}
                    />
                    <Route
                      path={ROUTES.INTERNAL_MARKET_REVIEW}
                      element={<InternalMarketPage />}
                    />
                    {/* VRG-537 — one piece of content: problem/solution + assigned
                        companies. `/:id` directly under the parent (not the task
                        file's `/content/:id`) — see ROUTES.INTERNAL_MARKET_CONTENT's
                        comment for why. Static beats dynamic in react-router's
                        ranking regardless of declaration order, so this cannot
                        shadow the two static sibling routes above. */}
                    <Route
                      path={ROUTES.INTERNAL_MARKET_CONTENT(':id')}
                      element={<InternalMarketContentPage />}
                    />
                    {/* VRG-224 — the staff docs site. Same components as /docs, pointed
                        at the internal artifact; inside <InternalRoute>, so the guard is
                        the one every other /internal/* route already uses. */}
                    <Route
                      path={ROUTES.INTERNAL_DOCS}
                      element={<Suspense fallback={<Loader />}><InternalDocsPage /></Suspense>}
                    />
                    <Route
                      path={`${ROUTES.INTERNAL_DOCS}/:category/:slug`}
                      element={<Suspense fallback={<Loader />}><InternalDocPage /></Suspense>}
                    />
                  </Route>
                  {/* Catch-all inside the Layout: authed users get a 404 with the
                      sidebar still visible (VERA-506). */}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>

              <Route element={<UnProtectedRoute />}>
                {/* VERA-1095: every ambient auth screen shares ONE backdrop,
                    rendered by this layout route. The pages used to render
                    `SignInBackground` themselves, which meant navigating
                    sign-in -> sign-up destroyed an 1800px blur(250px) layer and
                    rasterised a new one — a visible flash, mid-cross-fade. A
                    layout route stays mounted while only the child swaps, so the
                    expensive layer is rasterised once for the whole auth
                    session. It also gives the backdrop colour a single,
                    theme-aware owner instead of four `#000000` literals.

                    These pages still own their own `position: fixed` shells for
                    LAYOUT — centring, scrolling, per-page overflow. They just no
                    longer paint a background.

                    NOT LayoutAuth: that one renders an empty fixed header at
                    zIndex 100 with an opaque background, which painted over the
                    sign-up wordmark when it was tried, and pins its outlet to
                    440px, which the 3-card signup layout escapes anyway. */}
                <Route element={<LayoutAuthAmbient />}>
                  <Route path={ROUTES.SIGN_IN} element={<SignInPage />} />
                  {/* VERA-1095: "forgot password" link destination —
                      unauthenticated, reuses PasswordSetup (reset mode
                      determined by route). */}
                  <Route path={ROUTES.RESET_PASSWORD} element={<PasswordSetup />} />
                  {/* VERA-1095: signup email-verification link destination —
                      unauthenticated, auto-verifies the token on mount. */}
                  <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />
                  <Route path={ROUTES.SIGN_UP} element={<SignUpPage />} />
                  {/* VRG-134: self-serve signup (no invite code in the URL) */}
                  <Route path={ROUTES.SIGN_UP_SELF_SERVE} element={<SignUpPage />} />
                  {/* Stripe returns here after payment (VERA-1095 slice 4).

                      MOVED under LayoutAuthAmbient. It used to sit under plain
                      LayoutAuth, which meant the ambient blob field was not on
                      this page at all — so the backdrop the visitor had been
                      looking at for four steps vanished at the exact moment of
                      success. Sharing the layout with the signup route also
                      means the field is not re-mounted by the navigation, which
                      is what lets the success screen ACCELERATE the same blobs
                      rather than fade in a second set (see `ambientSurge`).

                      It brings its own centred shell, like every other page
                      under this layout, rather than borrowing LayoutAuth's
                      440px column. */}
                  <Route path={ROUTES.SIGN_UP_SUCCESS} element={<SignupSuccessPage />} />
                  {/* SSO / AuthKit redirect landing (VRG-646). MOVED under
                      LayoutAuthAmbient — the same omission SIGN_UP_SUCCESS had.
                      It sat beside this layout, not in it, so every state it
                      renders (the 2FA "Verify it's you" challenge for an admin
                      signing in with Google/Apple, enrollment-required, the
                      spinner and the error) showed on the bare page instead of
                      the backdrop sign-in had just shown. Every route in the
                      UnProtectedRoute group belongs inside this layout;
                      `test/pre-auth-routes.guard.spec.ts` now fails if one
                      is added outside it. */}
                  <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallbackPage />} />
                </Route>
              </Route>

              {/* VRG-361: standalone — reachable while authenticated but console-trial
                  gated, deliberately outside both ProtectedRoute (most of its data calls
                  now 403) and UnProtectedRoute (the user has a valid token). */}
              <Route path={ROUTES.TRIAL_ENDED} element={<TrialEndedPage />} />

              {/* The PUBLIC support page (the app stores' required support URL).
                  Outside BOTH guards for the same reason TRIAL_ENDED is: a signed-OUT
                  reviewer must reach it (ProtectedRoute would bounce them to /sign-in)
                  and a signed-IN operator following the app's "Contact support" link
                  must reach it too (UnProtectedRoute would redirect them away). */}
              <Route path={ROUTES.HELP} element={<PublicHelpPage />} />

              <Route path="*" element={<NotFoundPage fullScreen />} />
            </Routes>
  );
};

/**
 * VRG-427 — records the last route that rendered successfully, so a crash
 * elsewhere in the tree can send the user back to it. Must be mounted INSIDE
 * the router (it is, here — `BrowserRouter` wraps `<App/>` in `index.tsx`) but
 * OUTSIDE anything that might itself be what crashes, so its own effect keeps
 * firing on every healthy navigation. See `utils/routeHistory.ts`.
 */
const RouteRecoveryTracker = () => {
  useTrackRouteForRecovery();
  return null;
};

export const App = () => {
  useTextZoom();
  return (
    <ErrorBoundary>
      {/* VRG-600: the two props carry the website hand-off. `initialMode` is the
          `?theme=` vera.inc put on its Log-in link (localStorage is per-origin, so
          it cannot reach us any other way); `ignoreStoredMode` stops THIS origin's
          stored value — an authenticated-app preference, written only by the
          signed-in theme control and ThemeHydrator below — from answering for a
          stranger who has just arrived on a signup or verify link. Both are
          resolved at module load; see utils/colorModeHandoff. */}
      <VeraThemeProvider
        initialMode={HANDOFF_COLOR_MODE ?? undefined}
        ignoreStoredMode={ENTERED_PRE_AUTH}
      >
        <ThemeHydrator />
        {/* The <linearGradient> that paints a SELECTED nav icon's stroke, in both the
            sidebar rail and the Settings nav. Mounted exactly once, here, because SVG
            resolves url(#id) to the FIRST match in the document — a def duplicated per
            icon would not error, it would silently paint every icon from whichever copy
            came first. Inside the theme provider: its stops are mode-aware. */}
        <SelectedNavIconGradientDef />
        <DevToolsProvider>
          <CartProvider>
            {/* Outside <Routes> on purpose: it has to see the REAL location to
                know the URL is the intake and what the page behind it was. */}
            <SupportIntakeProvider>
              <RouteRecoveryTracker />
              {/* VRG-427: shows "this has been reported to Vera" on the page the
                  user is redirected BACK to after a crash — never on the dead
                  screen itself. Mounted once, here, so it is present regardless
                  of which route the redirect lands on. */}
              <ErrorReportedModal />
              {/* Decides once per render whether this reader sees the internal glossary
                  entries, so the tooltips and the glossary page do not each have to ask.
                  Sits inside the query provider because it reads the signed-in role. */}
              <GlossaryProvider>
                <AppRoutes />
              </GlossaryProvider>
            </SupportIntakeProvider>
          </CartProvider>
        </DevToolsProvider>
      </VeraThemeProvider>
    </ErrorBoundary>
  );
};
